import { createPortal } from 'react-dom';
import { Star, X, Loader2 } from 'lucide-react';
import RatingStars from './RatingStars';
import RatingForm from './RatingForm';
import useRatingData from '../hooks/useRatings';

/**
 * Full-screen overlay for viewing and submitting ratings
 */
export default function RatingsOverlay({ userId, userName, onClose }) {
  const { ratings, avg, loading, reload, submitRating, loadMore, hasMore } = useRatingData(userId);

  function StarsRow({ value }) {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((n) => (
          <Star
            key={n}
            className={`h-3.5 w-3.5 ${n <= value ? 'fill-primary text-primary' : 'text-muted-foreground/40'}`}
          />
        ))}
      </div>
    );
  }

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm" />
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative mx-4 flex max-h-[85vh] w-full max-w-md flex-col rounded-3xl bg-card shadow-2xl"
      >
        <div className="flex items-center justify-between p-5 pb-2">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold">
              Reviews {userName ? `for ${userName}` : ''}
            </h3>
            {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          </div>
          <button onClick={onClose}>
            <X className="h-5 w-5 text-muted-foreground" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 pb-5">
          {/* Aggregate */}
          <div className="rounded-xl border border-border bg-secondary/40 px-4 py-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Overall rating</span>
              <div className="flex items-center gap-2">
                <StarsRow value={Math.round(avg)} />
                <span className="text-sm font-semibold">{avg.toFixed(1)}</span>
                <span className="text-xs text-muted-foreground">({ratings.length})</span>
              </div>
            </div>
          </div>

          {/* Write a review */}
          <div className="mt-1">
            <RatingForm
              ratings={ratings}
              submitRating={submitRating}
              reload={reload}
            />
          </div>

          {/* List */}
          {ratings.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">No reviews yet.</p>
          ) : (
            <>
              {ratings.map((r) => (
                <div key={r.id} className="mt-1 rounded-xl border border-border p-3">
                  <div className="flex items-center justify-between">
                    <StarsRow value={r.stars} />
                    {r.met_in_person && (
                      <span className="rounded-full bg-success/20 px-2 py-0.5 text-[10px] font-medium text-success">
                        Met in person
                      </span>
                    )}
                  </div>
                  {r.comment && (
                    <p className="mt-2 text-sm text-foreground">{r.comment}</p>
                  )}
                </div>
              ))}
              {hasMore && (
                <button
                  onClick={loadMore}
                  className="mt-1 w-full rounded-xl border border-border bg-secondary/40 py-3 text-sm font-medium text-foreground transition hover:bg-secondary/60"
                >
                  Load more reviews
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
